import { useCallback, useEffect, useRef, useState } from 'react';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { DRIVE_KINDS, DRIVE_STATUSES, driveCapacity, type DriveListRow, type DriveStatus } from '../../shared/drives';
import { api } from '../lib/api';
import { useIsMobile } from '../lib/useIsMobile';
import { Seg } from './primitives';
import { DriveEditor } from './DriveEditor';
import { DrivesBoard } from './DrivesBoard';

export function DriveStatusChip({ status }: { status: DriveStatus }) {
  return <span className={`dstat ${status}`}>{status}</span>;
}

export function EditingDriveTag() {
  return <span className="drv-editing-tag" title="Pinned first and always open on Drives → Active. Set in Settings → Registers.">Editing drive</span>;
}

/** Move relative to the visible neighbour without dropping hidden drive IDs. */
export function reorderDrives(drives: DriveListRow[], active: string, over: string): DriveListRow[] {
  const from = drives.findIndex(d => d.id === active), to = drives.findIndex(d => d.id === over);
  return from < 0 || to < 0 || from === to ? drives : arrayMove(drives, from, to);
}

export function SortableDrive({ drive: d, mobile, disabled, onEdit }: {
  drive: DriveListRow; mobile: boolean; disabled: boolean; onEdit: () => void;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: d.id, disabled });
  const style = { transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined, transition };
  const grip = <button type="button" ref={setActivatorNodeRef} className="drive-order-grip" disabled={disabled}
    {...attributes} {...listeners} aria-label={`Reorder ${d.label}`} onClick={e => e.stopPropagation()}>
    <svg viewBox="0 0 10 14" fill="currentColor" aria-hidden="true">{[3, 7, 11].flatMap(y => [3, 7].map(x => <circle key={`${x}-${y}`} cx={x} cy={y} r="1" />))}</svg>
  </button>;
  if (mobile) return <div ref={setNodeRef} style={style} className={`drive-card has-grip ${d.status}${isDragging ? ' dragging' : ''}`} onClick={onEdit}>
    {grip}<button className="nm drive-label-button" onClick={e => { e.stopPropagation(); onEdit(); }}>{d.label}</button>{!!d.is_editing_drive && <EditingDriveTag />}<DriveStatusChip status={d.status} />
    <span className="meta"><span className="kind">{d.kind}</span><span>{driveCapacity(d.capacity_gb)}</span><span>{d.location}</span><span>{d.project_count} {d.project_count === 1 ? 'project' : 'projects'}</span></span>
    {d.notes && <span className="note">{d.notes}</span>}
  </div>;
  return <tr ref={setNodeRef} style={style} className={`${d.status}${isDragging ? ' dragging' : ''}`} onClick={onEdit}>
    <td className="grip">{grip}</td><td className="lbl"><button className="drive-label-button" onClick={e => { e.stopPropagation(); onEdit(); }}>{d.label}</button>{!!d.is_editing_drive && <EditingDriveTag />}</td>
    <td><span className="kind">{DRIVE_KINDS.find(k => k.value === d.kind)?.label}</span></td><td className="num">{driveCapacity(d.capacity_gb)}</td>
    <td>{d.location || '—'}</td><td className="mono dim">{d.serial || '—'}</td><td className="mono">{d.purchased_on || '—'}</td>
    <td><DriveStatusChip status={d.status} /></td><td className="num">{d.project_count}</td><td className="note" title={d.notes ?? ''}>{d.notes || '—'}</td>
  </tr>;
}

export function DrivesRegister() {
  const [drives, setDrives] = useState<DriveListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<DriveStatus | 'all'>('active');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<DriveListRow | 'new' | null>(null);
  const [view, setView] = useState<'table' | 'board'>(() => {
    try { return sessionStorage.getItem('drives.view') === 'board' ? 'board' : 'table'; } catch { return 'table'; }
  });
  const [savingOrder, setSavingOrder] = useState(false);
  const [boardVersion, setBoardVersion] = useState(0);
  const busy = useRef(false);
  const mobile = useIsMobile();
  // Only the grip captures pointer gestures, leaving phone cards free to scroll.
  // Keyboard sorting (Space, arrows, Space) is also available on phone.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const load = useCallback(async () => {
    try { setDrives((await api<{ drives: DriveListRow[] }>('/api/admin/drives')).drives); setError(''); }
    catch (e) { setError((e as Error).message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  const changeView = (next: 'table' | 'board') => {
    if (busy.current) return;
    setView(next);
    try { sessionStorage.setItem('drives.view', next); } catch { /* Storage can be disabled. */ }
  };
  const drop = async ({ active, over }: DragEndEvent) => {
    if (!over || busy.current) return;
    const next = reorderDrives(drives, String(active.id), String(over.id));
    if (next === drives) return;
    busy.current = true; setSavingOrder(true); setError(''); setDrives(next);
    try { await api('/api/admin/drives/order', { method: 'PUT', json: { ids: next.map(d => d.id) } }); }
    catch (e) { setDrives(drives); setError((e as Error).message); }
    finally { busy.current = false; setSavingOrder(false); }
  };
  const query = search.trim().toLowerCase();
  const visible = drives.filter(d => (filter === 'all' || d.status === filter) && [d.label, d.serial, d.location].some(s => s?.toLowerCase().includes(query)));
  const done = () => { setEditing(null); void load(); setBoardVersion(n => n + 1); };
  const rows = visible.map(d => <SortableDrive key={d.id} drive={d} mobile={mobile} disabled={savingOrder} onEdit={() => { if (!busy.current) setEditing(d); }} />);
  return <section className="drives-register" aria-label="Drives register">
    <div className="reg-head"><h2>Drives</h2><span className="group-count">{drives.length} · {drives.filter(d => d.status === 'active').length} active · {driveCapacity(drives.reduce((n, d) => n + (d.capacity_gb ?? 0), 0))}</span>
      {view === 'table' && <><input className="f-input drive-search" type="search" aria-label="Search drives" placeholder="Search label, serial, location…" value={search} onChange={e => setSearch(e.target.value)} />
        <Seg options={[...DRIVE_STATUSES, { value: 'all', label: 'All' }]} value={filter} onChange={setFilter} /></>}
      <div className="drive-view-seg"><Seg options={[{ value: 'table', label: 'Table' }, { value: 'board', label: 'Board' }]} value={view} onChange={changeView} /></div>
      <button className="btn primary" disabled={savingOrder} onClick={() => setEditing('new')}>+ Add drive</button>
    </div>
    {error && <p className="drive-error" role="alert">{error} <button className="btn" disabled={savingOrder} onClick={() => void load()}>Retry</button></p>}
    {view === 'board' ? <DrivesBoard key={boardVersion} onChanged={load} /> : <>
      <div className="drive-order-cap"><h3>Picker order</h3><p>Drag a row by its grip to set the order drives appear in the project drive picker.</p></div>
      {loading && <p role="status">Loading drives…</p>}
      {savingOrder && <p className="reg-sub" role="status">Saving picker order…</p>}
      {!loading && !error && !visible.length && <p className="reg-sub">{drives.length ? 'No drives match this filter.' : 'No drives yet. Add your first drive to keep track of project archives.'}</p>}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={drop}>
        <SortableContext items={visible.map(d => d.id)} strategy={verticalListSortingStrategy}>
          {mobile ? <div className="drive-cards">{rows}</div> : <div className="drive-table-scroll"><table className="reg-table"><thead><tr>
            <th className="grip" aria-label="Picker order" />{['Label', 'Kind', 'Capacity', 'Location', 'Serial', 'Purchased', 'Status', 'Projects', 'Notes'].map(h => <th key={h} className={h === 'Capacity' || h === 'Projects' ? 'num' : ''}>{h}</th>)}
          </tr></thead><tbody>{rows}</tbody></table></div>}
        </SortableContext>
      </DndContext>
    </>}
    {editing && <DriveEditor initial={editing === 'new' ? undefined : editing} editingDriveLabel={drives.find(d => d.is_editing_drive && d !== editing)?.label ?? null} onClose={() => setEditing(null)} onSaved={done} onDeleted={done} />}
  </section>;
}
